import { JSX, Match, Switch } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { getSvgIcon, TempSvgIcon } from '../icons/get-svg-icon';
import { IconSize } from './icon-size';

export interface IconProps {
  icon: string | TempSvgIcon;
  url?: string;
  size?: IconSize;
  /** The class prop is applied to the <i> element, or passed in as customClass to <modus-wc-icon>*/
  class?: string;
  /** This value is applied to the modus-wc-icon element */
  modusHostClass?: string;
}

/**
 * This Icon wrapper returns either <i> element that uses the Connect icon set
 * if the `icon` prop is passed in, and the icon name starts with `tc-`. If an
 * icon from the Modus icon set is passed in, it will return a `modus-wc-icon` element.
 * For the cases where the icon is only available on Figma, the svg content can be added to
 * the body of the getSvgIcon function with a key starting with svg-.
 * */
const Icon = (props: IconProps): JSX.Element => {
  function toModusSize(s: IconSize): 'sm' | 'md' | 'lg' {
    switch (s) {
      case 'icon-small':
        return 'sm';
      case 'icon-medium':
        return 'md';
      case 'icon-large':
      case 'icon-xlarge': // Modus doesn't support xlarge at the moment.
        return 'lg';
    }
  }

  const iconSizeClass = (): string => {
    switch (props.size) {
      case 'icon-small':
        return 'i16';
      case 'icon-large':
        return 'i32';
      case 'icon-xlarge':
        return 'text-icon-48';
      default:
        return '';
    }
  };

  const imgSizeClass = (): string => {
    switch (props.size) {
      case 'icon-small':
        return 'w-4 h-4';
      case 'icon-medium':
      case undefined:
        return 'w-5 h-5';
      case 'icon-large':
        return 'w-8 h-8';
      case 'icon-xlarge':
        return 'w-12 h-12';
      default:
        throw new UnreachableCaseError(props.size);
    }
  };

  const isSvgIcon = (): boolean => props.icon.startsWith('svg-');
  const isTcIcon = (): boolean => {
    // some icon props are passed in with "icon-font" and other classes
    // at the start which forces the modus-wc-icon fallback which then fails,
    // so check for tc- and some characters after it
    return /tc-[^\s]+/.test(props.icon);
  };

  return (
    <Switch
      fallback={
        <modus-wc-icon
          class={
            (props.modusHostClass || '') + ' flex justify-center items-center'
          }
          attr:custom-class={
            (props.class || '') + ' flex justify-center items-center'
          }
          name={props.icon}
          size={toModusSize(props.size || 'icon-medium')}
        />
      }
    >
      <Match when={props.url}>
        <img
          src={props.url}
          alt={props.icon}
          class={`${imgSizeClass()} object-contain ${props.class ?? ''}`}
        />
      </Match>
      <Match when={isTcIcon()}>
        <i
          class={`icon-font flex justify-center items-center ${props.icon} ${props.class ?? ''} ${iconSizeClass()}`}
          aria-hidden="true"
        />
      </Match>
      <Match when={isSvgIcon()}>
        <span class={props.class}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {getSvgIcon(props.icon as TempSvgIcon, props.size || 'icon-medium')}
        </span>
      </Match>
    </Switch>
  );
};

export default Icon;
